import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MakyLockup, MakyMascot } from "@/components/brand/MakyMark";
import { AppShell } from "@/components/layout/AppShell";
import { CreateOrderForm } from "@/components/order/CreateOrderForm";
import { Badge, Button, ListCell, Skeleton, Spinner, TextField } from "@/components/ui";
import { useCreateWorkspace, useOrders } from "@/lib/api/queries";
import { copyText } from "@/lib/clipboard";
import { useCurrentWorkspace, useWorkspaceStore } from "@/stores/workspace";

function CreateWorkspaceForm() {
  const [name, setName] = useState("");
  const createWorkspace = useCreateWorkspace();
  const setWorkspaceId = useWorkspaceStore((s) => s.setWorkspaceId);

  function handleCreate() {
    if (!name.trim()) return;
    createWorkspace.mutate(name, {
      onSuccess: (workspace) => {
        setWorkspaceId(workspace.id);
        toast.success(`${workspace.name} 워크스페이스를 만들었어요`);
      },
      onError: () => toast.error("만들지 못했어요. 다시 시도해 주세요."),
    });
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-4 p-6">
      <div>
        <MakyLockup size={30} />
        <h1 className="mt-3 text-heading-1 font-bold text-label-normal">막냉이</h1>
        <p className="mt-2 text-body-2 text-label-alternative">
          팀 이름으로 워크스페이스를 만들어 주세요.
        </p>
      </div>
      <TextField
        label="워크스페이스 이름"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="예) 영업관리팀"
      />
      <Button onClick={handleCreate} disabled={createWorkspace.isPending}>
        만들기
      </Button>
    </main>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { workspace, isLoading: workspacesLoading } = useCurrentWorkspace();
  const { data: orders, isLoading: ordersLoading } = useOrders(workspace?.id ?? "");

  if (workspacesLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!workspace) {
    return <CreateWorkspaceForm />;
  }

  // 함수 선언은 호이스팅돼서 위 null 체크의 좁히기가 안 따라온다 — 값만 꺼내 둔다.
  const inviteCode = workspace.invite_code;

  async function handleCopyInvite() {
    const ok = await copyText(`${location.origin}/join/${inviteCode}`);
    if (ok) toast.success("초대 링크를 복사했어요");
    else toast.error("복사하지 못했어요");
  }

  async function handleCopyOrderLink(slug: string) {
    const ok = await copyText(`${location.origin}/o/${slug}`);
    if (ok) toast.success("링크를 복사했어요");
    else toast.error("복사하지 못했어요");
  }

  return (
    <AppShell title="주문">
      {/* 워크스페이스 이름은 헤더의 전환 셀렉트가 보여준다 — 여기서 또 쓰지 않는다. */}
      <div className="flex items-center justify-end">
        <Button variant="text" size="small" onClick={handleCopyInvite}>
          팀원 초대하기
        </Button>
      </div>

      <div className="mt-4">
        <CreateOrderForm workspaceId={workspace.id} />
      </div>

      <h2 className="mt-8 text-label-1 font-semibold text-label-normal">주문 목록</h2>
      {ordersLoading ? (
        // 스피너 대신 줄 모양을 미리 잡는다 — 응답이 오는 순간 아래가 밀리지 않는다.
        <Skeleton rows={3} className="mt-3" />
      ) : orders && orders.length > 0 ? (
        <ul className="mt-2">
          {orders.map((order) => (
            <li key={order.id}>
              <ListCell
                title={order.menuboard_name}
                description={`${order.submitted_count} / ${order.person_count}명 제출`}
                trailing={
                  <div className="flex items-center gap-2">
                    <Badge type="text" tone={order.status === "open" ? "positive" : "neutral"}>
                      {order.status === "open" ? "받는 중" : "마감"}
                    </Badge>
                    {/* trailing 슬롯은 줄 덮개보다 위에 있어서 각자 눌린다
                        (`ListCell`) — stopPropagation 이 필요 없다. */}
                    <Button variant="text" size="small" onClick={() => handleCopyOrderLink(order.slug)}>
                      링크 복사
                    </Button>
                  </div>
                }
                chevron
                onClick={() => navigate(`/orders/${order.id}`)}
              />
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-6 flex flex-col items-center gap-3 text-center">
          <MakyMascot size={104} />
          <p className="text-body-2 text-label-alternative">
            아직 만든 주문이 없어요. 링크부터 하나 만들어 볼까요?
          </p>
        </div>
      )}
    </AppShell>
  );
}
